import { Search, LayoutGrid, List } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { locations, categories, statuses, types, budgets } from '../../data/projects';

export const FilterBar = ({
  filters,
  setFilters,
  viewMode,
  setViewMode,
  showViewToggle = true,
  compact = false,
  projects = [],
  onSearch,
}) => {
  // Dynamically compute locations list based on actual projects from the database
  const uniqueLocations = Array.from(new Set(projects.map(p => p.area).filter(Boolean)));
  const locationsList = uniqueLocations.length > 0 ? ['All Locations', ...uniqueLocations] : locations;
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === `All ${key.charAt(0).toUpperCase() + key.slice(1)}s` || value === 'All Status' || value === 'All Budgets' ? '' : value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      location: '',
      category: '',
      status: '',
      type: '',
      budget: '',
    });
  };

  const hasActiveFilters = Object.values(filters).some((v) => v);

  if (compact) {
    return (
      <div
        data-testid="filter-bar"
        className="flex flex-wrap items-center gap-4 p-4 bg-white border border-slate-100 rounded-2xl shadow-sm"
      >
        <Select
          value={filters.location || 'All Locations'}
          onValueChange={(v) => handleFilterChange('location', v)}
        >
          <SelectTrigger className="w-[160px]" data-testid="filter-location">
            <SelectValue placeholder="Location" />
          </SelectTrigger>
          <SelectContent>
            {locationsList.map((loc) => (
              <SelectItem key={loc} value={loc}>
                {loc}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.type || 'All Types'}
          onValueChange={(v) => handleFilterChange('type', v)}
        >
          <SelectTrigger className="w-[160px]" data-testid="filter-type">
            <SelectValue placeholder="Property Type" />
          </SelectTrigger>
          <SelectContent>
            {types.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.budget || 'All Budgets'}
          onValueChange={(v) => handleFilterChange('budget', v)}
        >
          <SelectTrigger className="w-[180px]" data-testid="filter-budget">
            <SelectValue placeholder="Budget" />
          </SelectTrigger>
          <SelectContent>
            {budgets.map((budget) => (
              <SelectItem key={budget} value={budget}>
                {budget}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          onClick={onSearch}
          data-testid="search-projects-btn"
          className="bg-[#47ace2] hover:bg-[#1f7db3] text-white rounded-full px-8"
        >
          <Search className="w-4 h-4 mr-2" />
          Search
        </Button>
      </div>
    );
  }

  return (
    <div
      data-testid="filter-bar"
      className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm"
    >
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Select
            value={filters.location || 'All Locations'}
            onValueChange={(v) => handleFilterChange('location', v)}
          >
            <SelectTrigger data-testid="filter-location">
              <SelectValue placeholder="Location" />
            </SelectTrigger>
            <SelectContent>
              {locationsList.map((loc) => (
                <SelectItem key={loc} value={loc}>
                  {loc}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.category || 'All Categories'}
            onValueChange={(v) => handleFilterChange('category', v)}
          >
            <SelectTrigger data-testid="filter-category">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.type || 'All Types'}
            onValueChange={(v) => handleFilterChange('type', v)}
          >
            <SelectTrigger data-testid="filter-type">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              {types.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.status || 'All Status'}
            onValueChange={(v) => handleFilterChange('status', v)}
          >
            <SelectTrigger data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {statuses.map((status) => (
                <SelectItem key={status} value={status}>
                  {status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.budget || 'All Budgets'}
            onValueChange={(v) => handleFilterChange('budget', v)}
          >
            <SelectTrigger data-testid="filter-budget">
              <SelectValue placeholder="Budget" />
            </SelectTrigger>
            <SelectContent>
              {budgets.map((budget) => (
                <SelectItem key={budget} value={budget}>
                  {budget}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-3">
          {hasActiveFilters && (
            <Button
              variant="ghost"
              onClick={clearFilters}
              data-testid="clear-filters-btn"
              className="text-slate-500 hover:text-slate-700"
            >
              Clear All
            </Button>
          )}

          {showViewToggle && (
            <div className="flex items-center gap-1 border border-slate-200 rounded-lg p-1">
              <button
                data-testid="view-grid-btn"
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-[#47ace2] text-white'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                data-testid="view-list-btn"
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list'
                    ? 'bg-[#47ace2] text-white'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
